import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Fx, Stack } from 'Components';
import { useStores } from 'Hooks/useStore';
import { DashboardContext, ToastType } from 'Pages/Dashboard/DashboardContext';
import DashboardFilters from 'Pages/Dashboard/DashboardFilters/DashboardFilters';
import { DashboardFiltersValues } from 'Pages/Dashboard/Dashboard.types';
import { getAppRoutes } from 'Pages/App/App.constants';
import HeaderRow from './components/HeaderRow';
import { useTranslation } from 'react-i18next';
import Toaster from 'PhishingManager/components/Toast';

const toastInitialValue: ToastType = { message: '', type: "warning", icon: "check" }
function TenantProvider({ children, showHeader }: { children: React.ReactNode, showHeader: boolean }) {
    const stores = useStores();
	const { authStore, systemsStore } = stores;
    const isSuperAdmin = authStore.isCurrentUserSuperAdmin;
    const [filterValues, setFilterValues] = useState<DashboardFiltersValues>({});
    const [toast, setToast] = useState<ToastType>({ ...toastInitialValue });
    
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const [hasProperties, setHasProperties] = useState(true);
    const appRoutes = getAppRoutes();

    const onDashboardFiltersChange = (value: DashboardFiltersValues) => {
        setFilterValues({ ...filterValues, ...value });
    };

    const setToastInfo = (value: ToastType) => setToast(value);

    const componentDetails = useMemo(() => {
        let details = null;
        const normalizedPath = pathname === '/' ? pathname : pathname.replace(/\/+$/, '');
        if (appRoutes.USER_ACTIVITY === normalizedPath) {
            details = {
                title: t("pageHeader.userActivity.title"),
                subTitle: t("pageHeader.userActivity.description"),
                showFilter: true
            }
        } else if (appRoutes.PHISHING === normalizedPath) {
            details = {
                title: t("pageHeader.phishing.title"),
                subTitle: t("pageHeader.phishing.description"),
                showFilter: false
            }
        } else if (appRoutes.TRAINING === normalizedPath) {
            details = {
                title: t("pageHeader.training.title"),
                subTitle: t("pageHeader.training.description"),
                showFilter: true
            }
        } else if (appRoutes.COURSES === normalizedPath) {
            details = {
                title: t("pageHeader.courses.title"),
                subTitle: t("pageHeader.courses.description"),
                showFilter: isSuperAdmin
            }
        } else if (appRoutes.REPORTS === normalizedPath) {
            details = {
                title: t("pageHeader.library.title"),
                subTitle: t("pageHeader.library.description"),
                showFilter: false
            }
        } else if (appRoutes.SAVED_REPORTS === normalizedPath) {
            details = {
                title: t("pageHeader.savedReports.title"),
                subTitle: t("pageHeader.savedReports.description"),
                showFilter: false
            }
        } else if (appRoutes.HOME === normalizedPath) {
            details = {
                title: t("pageHeader.dashboard.title"),
                subTitle: t("pageHeader.dashboard.description"),
                showFilter: true
            }
        } else if (appRoutes.PROFILE === normalizedPath) {
            details = {
                title: t("pageHeader.profile.title"),
                subTitle: t("pageHeader.profile.description"),
                showFilter: false
            }
        }
        return details;
    }, [pathname, isSuperAdmin, t]);

    useEffect(() => {
		const tenantId = systemsStore.selectedTenant.value;
		if(!isSuperAdmin && tenantId && !filterValues.id) onDashboardFiltersChange({ id: tenantId });
	}, [systemsStore])

    useEffect(() => {
        return () => stores.clearDashboardStore();
    }, []);

    return (
        <DashboardContext.Provider value={{ filters: filterValues, hasProperties, setToastInfo }}>
            <Fx column grow={1} style={{ display: showHeader ? "block" : "none" }}>
                <Stack>
                    <HeaderRow title={componentDetails?.title || ""} subHeading={componentDetails?.subTitle || ""}>
                        <DashboardFilters {...{ filterValues, isSuperAdmin }} showFilter={componentDetails?.showFilter} onChange={onDashboardFiltersChange} onSetHasProperties={setHasProperties} />
                    </HeaderRow>
                    {showHeader ? children : null}
                </Stack>
            </Fx>
            {showHeader ? null : children}
            <Toaster visible={!!toast.message} message={toast?.message ?? ''} type={toast?.type ?? 'warning'} icon={toast?.icon || "campaignInfo"} onClose={() => setToastInfo({ ...toastInitialValue })} />
        </DashboardContext.Provider>
    );
}

export default TenantProvider;